import { useMemo, type FC } from 'react';
import { LuCheck, LuClock, LuTriangleAlert, LuUsers } from 'react-icons/lu';
import type { PerformanceEquipe, PerformanceEquipeMembro } from '../services/RelatorioService';
import type { Opportunity } from '../services/OpportunityService';
import { emAndamento, gruposDoFunil } from '../data/etapas';
import type { GoalsTimeframe } from './IndividualTab';
import './TeamsTab.css';

interface WorkingDaysInfo {
    monthlyDays: number;
    weeklyDays: number;
    elapsedDays: number;
}

interface TeamsTabProps {
    team: PerformanceEquipe | null;
    members: PerformanceEquipeMembro[];
    isLoadingMembers: boolean;
    opportunities: Opportunity[];
    workingDaysInfo: WorkingDaysInfo;
    timeframe: GoalsTimeframe;
    onTimeframeChange: (tf: GoalsTimeframe) => void;
    /** Abre o dossiê da pessoa na aba Individual. */
    onMemberClick: (usuarioId: number) => void;
}

const TIMEFRAMES: { id: GoalsTimeframe; label: string; noun: string }[] = [
    { id: 'month', label: 'Mês', noun: 'no mês' },
    { id: 'week', label: 'Semana', noun: 'na semana' },
    { id: 'day', label: 'Dia', noun: 'no dia' },
];

/* As etapas abertas são o miolo do funil da empresa — etapa que ela criar entra sozinha. */
const openStagesDoFunil = (): { key: number; name: string }[] =>
    gruposDoFunil().filter(g => g.plus).map(g => ({ key: g.alvo, name: g.title }));

const formatCurrencyShort = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value || 0);

const formatDecimal = (value: number) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const initialsOf = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

const describeDays = (days: number | null) => {
    if (days === null) return 'sem registro';
    if (days === 0) return 'hoje';
    if (days === 1) return 'ontem';
    return `há ${days} dias`;
};

/**
 * Aba Equipes — Direção B: a equipe é o conjunto de pessoas. Os membros ficam
 * na primeira dobra, com ritmo e última atividade; funil e atividade somada
 * são apoio. Reaproveita os tokens e as barras da Individual.
 */
const TeamsTab: FC<TeamsTabProps> = ({
    team,
    members,
    isLoadingMembers,
    opportunities,
    workingDaysInfo,
    timeframe,
    onTimeframeChange,
    onMemberClick,
}) => {
    const memberNames = useMemo(() => new Set(members.map(m => m.usuarioNome)), [members]);

    const teamDeals = useMemo(
        () => opportunities.filter(op => memberNames.has(op.nomeUsuarioResponsavel ?? '')),
        [opportunities, memberNames]);

    /* Última atividade por pessoa: mesma regra do dossiê individual. */
    const lastActivityByName = useMemo(() => {
        const map = new Map<string, number>();
        teamDeals.forEach(op => {
            const name = op.nomeUsuarioResponsavel ?? '';
            op.detalhesConversa?.forEach(det => {
                const t = new Date(det.dataDetalheCriado).getTime();
                if (!map.has(name) || t > map.get(name)!) map.set(name, t);
            });
        });
        const now = Date.now();
        const days = new Map<string, number | null>();
        memberNames.forEach(name => {
            const t = map.get(name);
            days.set(name, t === undefined ? null : Math.floor(Math.abs(now - t) / (1000 * 60 * 60 * 24)));
        });
        return days;
    }, [teamDeals, memberNames]);

    const openStages = useMemo(() => {
        const stagesDoFunil = openStagesDoFunil();
        const rows = stagesDoFunil.map(stage => ({
            name: stage.name,
            count: 0,
            value: 0,
        }));
        teamDeals.forEach(op => {
            const idx = stagesDoFunil.findIndex(s => s.key === op.idStauts);
            if (idx >= 0) {
                rows[idx].count++;
                rows[idx].value += op.valor || 0;
            }
        });
        return rows;
    }, [teamDeals]);

    const openMax = useMemo(() => Math.max(...openStages.map(s => s.value), 1), [openStages]);

    const activity = useMemo(() => {
        const leads = teamDeals.length;
        const proposals = teamDeals.filter(op => emAndamento(op.idStauts)).length;
        const won = teamDeals.filter(op => op.idStauts === 3).length;
        return {
            leads,
            proposals,
            conversion: leads > 0 ? (won / leads) * 100 : 0,
        };
    }, [members, teamDeals]);

    const globalConversion = useMemo(() => {
        const won = opportunities.filter(op => op.idStauts === 3).length;
        return opportunities.length > 0 ? (won / opportunities.length) * 100 : 0;
    }, [opportunities]);

    /* Ritmo e projeção da equipe são somados no front: o endpoint
       ProjecaoFechamento pede usuarioId e não existe versão por equipe. */
    const pace = useMemo(() => {
        const realized = team?.totalFaturado ?? 0;
        const goal = team?.metaFaturamento ?? 0;
        const daily = realized / (workingDaysInfo.elapsedDays || 1);
        return {
            daily,
            required: goal / (workingDaysInfo.monthlyDays || 1),
            projection: daily * workingDaysInfo.monthlyDays,
            remaining: Math.max(workingDaysInfo.monthlyDays - workingDaysInfo.elapsedDays, 0),
        };
    }, [team, workingDaysInfo]);

    const scoped = useMemo(() => {
        const goal = team?.metaFaturamento ?? 0;
        const realized = team?.totalFaturado ?? 0;
        const perDayGoal = goal / (workingDaysInfo.monthlyDays || 1);
        const perDayRealized = realized / (workingDaysInfo.elapsedDays || 1);
        if (timeframe === 'day') return { goal: perDayGoal, realized: perDayRealized };
        if (timeframe === 'week') {
            return {
                goal: perDayGoal * workingDaysInfo.weeklyDays,
                realized: perDayRealized * workingDaysInfo.weeklyDays,
            };
        }
        return { goal, realized };
    }, [team, timeframe, workingDaysInfo]);

    const bullet = useMemo(() => {
        const scale = Math.max(scoped.realized, scoped.goal) * 1.1 || 1;
        const pctOf = (v: number) => Math.max(0, Math.min(100, (v / scale) * 100));
        const achievement = scoped.goal > 0 ? (scoped.realized / scoped.goal) * 100 : 0;
        return {
            fill: pctOf(scoped.realized),
            metaAt: pctOf(scoped.goal),
            achievement,
            beat: scoped.goal > 0 && scoped.realized >= scoped.goal,
            onTrack: scoped.goal > 0 && achievement >= 70 && achievement < 100,
            behind: scoped.goal > 0 && achievement < 70,
        };
    }, [scoped]);

    /* Régua comum aos membros, para 100% cair sempre no mesmo lugar. */
    const memberScale = useMemo(() => {
        const worst = Math.max(...members.map(m => m.percentualFaturamento), 100);
        return Math.max(worst, 100) * 1.05;
    }, [members]);

    const timeframeNoun = TIMEFRAMES.find(t => t.id === timeframe)?.noun ?? 'no mês';

    if (!team) {
        return (
            <div className="tt-empty chart-card">
                <LuUsers className="tt-empty-icon" />
                <h4>Nenhuma equipe para este mês</h4>
                <p>Sem dados de performance de equipes no período selecionado.</p>
            </div>
        );
    }

    return (
        <div className="teams-tab">

            {/* ═══ FAIXA DA EQUIPE ═════════════════════════════════ */}
            <section className="chart-card tt-strip">
                <div className="tt-strip-id">
                    <div className="tt-avatar">{initialsOf(team.equipeNome)}</div>
                    <div className="tt-id-text">
                        <span className="tt-name">{team.equipeNome}</span>
                        <span className="tt-count">
                            {members.length} {members.length === 1 ? 'pessoa' : 'pessoas'}
                        </span>
                    </div>
                </div>

                <div className="tt-strip-figure">
                    <strong className="tt-value">{formatCurrencyShort(scoped.realized)}</strong>
                    <span className="tt-of">de {formatCurrencyShort(scoped.goal)}</span>
                    {bullet.beat && <span className="an-chip an-chip-good"><LuCheck /> {Math.round(bullet.achievement)}%</span>}
                    {bullet.onTrack && <span className="an-chip an-chip-info"><LuClock /> {Math.round(bullet.achievement)}%</span>}
                    {bullet.behind && <span className="an-chip an-chip-warn"><LuTriangleAlert /> {Math.round(bullet.achievement)}%</span>}
                </div>

                {scoped.goal > 0 && (
                    <div className="tt-strip-bar">
                        <div className="an-bullet-track">
                            <div className="an-bullet-fill" style={{ width: `${bullet.fill}%` }} />
                            <div className="an-bullet-meta" style={{ left: `${bullet.metaAt}%` }} />
                        </div>
                        <span className="tt-strip-bar-note">meta {timeframeNoun}</span>
                    </div>
                )}

                <div className="tt-strip-stats">
                    <div className="tt-stat">
                        <span className="tt-stat-label">Ritmo</span>
                        <strong className="tt-stat-value">
                            {formatCurrencyShort(pace.daily)}<span className="tt-stat-unit">/dia</span>
                        </strong>
                    </div>
                    <div className="tt-stat">
                        <span className="tt-stat-label">Necessário</span>
                        <strong className="tt-stat-value is-muted">
                            {formatCurrencyShort(pace.required)}<span className="tt-stat-unit">/dia</span>
                        </strong>
                    </div>
                    <div className="tt-stat">
                        <span className="tt-stat-label">Projeção</span>
                        <strong className="tt-stat-value">{formatCurrencyShort(pace.projection)}</strong>
                    </div>
                    <div className="tt-stat">
                        <span className="tt-stat-label">Dias úteis restantes</span>
                        <strong className="tt-stat-value">
                            {pace.remaining}<span className="tt-stat-unit"> de {workingDaysInfo.monthlyDays}</span>
                        </strong>
                    </div>
                </div>

                <div className="tt-timeframe">
                    {TIMEFRAMES.map(tf => (
                        <button
                            key={tf.id}
                            type="button"
                            className={`tt-timeframe-btn ${timeframe === tf.id ? 'is-active' : ''}`}
                            onClick={() => onTimeframeChange(tf.id)}
                        >
                            {tf.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* ═══ MEMBROS — A PRIMEIRA DOBRA ══════════════════════ */}
            <section className="chart-card">
                <header className="an-card-head">
                    <h3>Quem faz a equipe</h3>
                    <span className="an-card-meta">clicar abre o dossiê da pessoa</span>
                </header>

                {isLoadingMembers ? (
                    <div className="tt-members">
                        {[0, 1, 2].map(i => (
                            <div key={i} className="tt-member-row">
                                <span className="skeleton skeleton-text-md" style={{ width: '140px' }} />
                                <span className="skeleton skeleton-text-sm" />
                                <span className="skeleton skeleton-text-sm" />
                                <span className="skeleton" style={{ height: '18px', borderRadius: '3px' }} />
                                <span className="skeleton skeleton-text-sm" />
                                <span className="skeleton skeleton-text-sm" />
                            </div>
                        ))}
                    </div>
                ) : members.length === 0 ? (
                    <p className="chart-empty">Esta equipe não tem membros com dados neste mês.</p>
                ) : (
                    <div className="tt-members">
                        <div className="tt-member-row tt-member-head">
                            <span>Colaborador</span>
                            <span className="is-right">Faturado</span>
                            <span className="is-right">Meta</span>
                            <span>Atingimento</span>
                            <span className="is-right">%</span>
                            <span className="is-right">Última atividade</span>
                        </div>

                        {members.map(member => {
                            const days = lastActivityByName.get(member.usuarioNome) ?? null;
                            const stale = days === null || days > 5;
                            const dailyCalls = member.totalLigacoes / (workingDaysInfo.elapsedDays || 1);
                            const hasGoal = member.metaFaturamento > 0;
                            return (
                                <button
                                    key={member.usuarioId}
                                    type="button"
                                    className="tt-member-row tt-member-item"
                                    onClick={() => onMemberClick(member.usuarioId)}
                                >
                                    <span className="tt-member-id">
                                        <span className="tt-member-avatar">{initialsOf(member.usuarioNome)}</span>
                                        <span className="tt-member-text">
                                            <span className="tt-member-name">{member.usuarioNome}</span>
                                            <span className="tt-member-sub">
                                                {member.totalLigacoes} ligações · {formatDecimal(dailyCalls)}/dia
                                            </span>
                                        </span>
                                    </span>
                                    <span className="is-right tt-member-money">{formatCurrencyShort(member.totalFaturado)}</span>
                                    <span className="is-right tt-member-goal">
                                        {hasGoal ? formatCurrencyShort(member.metaFaturamento) : '—'}
                                    </span>
                                    <span className="an-bar-track">
                                        <span
                                            className="an-bar-fill"
                                            style={{ width: `${hasGoal ? Math.min((member.percentualFaturamento / memberScale) * 100, 100) : 0}%` }}
                                        />
                                        <span className="an-bar-goal" style={{ left: `${(100 / memberScale) * 100}%` }} />
                                    </span>
                                    <span className="is-right tt-member-pct">
                                        {hasGoal ? `${Math.round(member.percentualFaturamento)}%` : '—'}
                                    </span>
                                    <span className={`is-right tt-member-last ${stale ? 'is-stale' : ''}`}>
                                        {describeDays(days)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* ═══ APOIO ═══════════════════════════════════════════ */}
            <div className="tt-support">
                <section className="chart-card">
                    <header className="an-card-head">
                        <h3>Funil da equipe</h3>
                        <span className="an-card-meta">etapas abertas</span>
                    </header>
                    <div className="tt-funnel">
                        {openStages.map(stage => (
                            <div key={stage.name} className="tt-funnel-row">
                                <span className="tt-funnel-name">{stage.name}</span>
                                <span className="an-bar-track">
                                    <span className="an-bar-fill" style={{ width: `${(stage.value / openMax) * 100}%` }} />
                                </span>
                                <span className={`is-right tt-funnel-value ${stage.value === 0 ? 'is-empty' : ''}`}>
                                    {stage.value > 0 ? formatCurrencyShort(stage.value) : 'vazia'}
                                </span>
                                <span className="is-right tt-funnel-count">{stage.count}</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="chart-card">
                    <header className="an-card-head">
                        <h3>Atividade somada</h3>
                    </header>
                    <div className="tt-activity">
                        <div className="tt-metric">
                            <span className="tt-metric-label">Ligações</span>
                            <strong className="tt-metric-value">{team.totalLigacoes}</strong>
                        </div>
                        <div className="tt-metric">
                            <span className="tt-metric-label">Leads contatados</span>
                            <strong className="tt-metric-value">{activity.leads}</strong>
                        </div>
                        <div className="tt-metric">
                            <span className="tt-metric-label">Em andamento</span>
                            <strong className="tt-metric-value">{activity.proposals}</strong>
                        </div>
                        <div className="tt-metric">
                            <span className="tt-metric-label">Conversão</span>
                            <strong className="tt-metric-value">
                                {formatDecimal(activity.conversion)}%
                                <span className={`tt-metric-cmp ${activity.conversion >= globalConversion ? 'is-good' : 'is-warn'}`}>
                                    empresa {formatDecimal(globalConversion)}%
                                </span>
                            </strong>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default TeamsTab;
